import { tracked } from '@glimmer/tracking';
import {
  Entities,
  Periods,
  MetricDataPoint,
  SPLIT_METRIC_MEASURES,
  Measure,
  SplitMetricMeasure,
} from 'cio/utils/metrics/common';
import Ember from 'ember';
import { CalculatedMeasure, calculateMeasure, DELIVERY_METRICS_MEASURE_DEFINITIONS, MeasureType } from '../measure';
import { computed } from '@ember/object';
import { TimeRange } from 'cio/utils/time';

export type UnderscoredMeasure = '_sent' | '_delivered' | '_opened' | '_clicked' | '_converted';

export const UNDERSCORED_MEASURES: Record<SplitMetricMeasure, UnderscoredMeasure> = {
  sent: '_sent',
  delivered: '_delivered',
  opened: '_opened',
  clicked: '_clicked',
  converted: '_converted',
};

export function computedMeasure(measure: Measure) {
  let measureDefinition = DELIVERY_METRICS_MEASURE_DEFINITIONS[measure];

  let dependentKeys: string[] = [];

  if (measureDefinition.isComputed) {
    dependentKeys = [...measureDefinition.dependentKeys];
  } else {
    dependentKeys = [measureDefinition.valueKey];
  }

  if (measureDefinition.denominator) {
    dependentKeys.push(measureDefinition.denominator);
  }

  // @ts-ignore the definition for computed doesn't seem to like it when we try
  // and destructure the dependent keys
  return computed(...dependentKeys.map((k) => `_${k}`), 'range', function (this: SplitMetric) {
    return calculateMeasure(
      measure as Measure,
      MeasureType.DeliveryMetrics,
      [this],
      this.rawMetrics as any,
      this.groupedMetrics,
    );
  }) as Ember.ComputedProperty<CalculatedMeasure>;
}

export class SplitMetric {
  entityId: number | string;
  entityType: Entities = Entities.ACTION;
  fetchedAt: number; // unix timestamp when this metric was received from the API
  period: Periods;
  range: TimeRange;

  actionType = 'email_action';
  canConvert = true;

  environmentId?: number;
  campaignId?: number;
  actionId?: string;
  templateId?: string;

  @tracked _sent: MetricDataPoint[] = [];
  @tracked _delivered: MetricDataPoint[] = [];
  @tracked _opened: MetricDataPoint[] = [];
  @tracked _clicked: MetricDataPoint[] = [];
  @tracked _converted: MetricDataPoint[] = [];

  get rawMetrics() {
    return {
      sent: this._sent,
      delivered: this._delivered,
      opened: this._opened,
      clicked: this._clicked,
      converted: this._converted,
    };
  }

  get groupedMetrics() {
    let startTimestamp = (this.range.start?.unix() ?? 0) * 1000;
    let endTimestamp = (this.range.end?.unix() ?? 0) * 1000;

    let metrics: Record<any, MetricDataPoint[]> = {};

    ['sent', 'delivered', 'opened', 'clicked', 'converted'].forEach((measure: SplitMetricMeasure) => {
      metrics[measure] = [...this[UNDERSCORED_MEASURES[measure]]].filter(
        (p) => p.x >= startTimestamp && p.x <= endTimestamp,
      );
    });

    return metrics;
  }

  @computedMeasure('sent') sent!: CalculatedMeasure;
  @computedMeasure('delivered') delivered!: CalculatedMeasure;
  @computedMeasure('opened') opened!: CalculatedMeasure;
  @computedMeasure('clicked') clicked!: CalculatedMeasure;
  @computedMeasure('converted') converted!: CalculatedMeasure;

  constructor(obj: any) {
    this.entityId = obj.entityId;
    this.entityType = obj.entityType;
    this.range = obj.range;

    this.period = obj.data.period;

    this.fetchedAt = Math.floor(Date.now() / 1000);

    if (obj.meta?.environmentId) {
      this.environmentId = obj.meta.environmentId;
    }

    if (obj.meta?.campaignId) {
      this.campaignId = obj.meta.campaignId;
    }

    this.actionId = String(obj.data.action_id);
    this.templateId = String(obj.data.template_id);

    SPLIT_METRIC_MEASURES.forEach((measure) => {
      // @ts-ignore
      this[`_${measure}`] = obj.measureData[measure];
    });
  }
}
